import { fetch_case_owner_cases } from '@/views/x-verdict_views/CaseOwner/AllCases/owner_case_queue_service';
import BlockingLoaderOverlay from '@/components/config_components/BlockingLoaderOverlay/BlockingLoaderOverlay.vue';
import {
  normalize_text,
  read_session_project_id,
} from '@/views/x-verdict_views/CompanyAdmin/CompanyCases/company_cases_common';

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const HOUR_PIXELS = 2.7;
const MIN_EVENT_HEIGHT = 3.9;
const OPEN_RUNTIME_STATUSES = new Set(['active', 'warning', 'breached', 'escalated']);
const TIME_SLOTS = ['08:00', '10:00', '12:00', '14:00', '16:00', '18:00'];

function start_of_day(date = new Date()) {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
}

function start_of_week(date = new Date()) {
  const result = start_of_day(date);
  const day = result.getDay() || 7;
  result.setDate(result.getDate() - day + 1);
  return result;
}

function start_of_month(date = new Date()) {
  const result = start_of_day(date);
  result.setDate(1);
  return result;
}

function add_days(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function same_day(left, right) {
  return left?.getFullYear() === right?.getFullYear()
    && left?.getMonth() === right?.getMonth()
    && left?.getDate() === right?.getDate();
}

function parse_due_date(value) {
  const date = new Date(value || '');
  return Number.isNaN(date.getTime()) ? null : date;
}

function time_label(date) {
  return date.toLocaleTimeString('en-ZA', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function date_label(date) {
  return date.toLocaleDateString('en-ZA', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

function day_head_label(date) {
  return date.toLocaleDateString('en-ZA', {
    weekday: 'short',
    day: '2-digit',
  });
}

function range_label(start_date, end_date) {
  return `${date_label(start_date)} - ${date_label(end_date)}`;
}

function month_label(date = new Date()) {
  return date.toLocaleDateString('en-ZA', {
    month: 'long',
    year: 'numeric',
  });
}

function compact_meta(parts = []) {
  return parts.filter(Boolean).join(' - ');
}

function event_top_rem(date) {
  const hour_value = date.getHours() + (date.getMinutes() / 60);
  const clamped_hour = Math.max(8, Math.min(18.5, hour_value));
  return ((clamped_hour - 8) * HOUR_PIXELS).toFixed(2);
}

function event_height_rem() {
  return MIN_EVENT_HEIGHT.toFixed(2);
}

function runtime_status_label(runtime = {}) {
  return normalize_text(runtime?.status_label || runtime?.status).toLowerCase();
}

function event_meta(case_item = {}) {
  return compact_meta([case_item?.status]);
}

function is_open_runtime(runtime = {}) {
  return OPEN_RUNTIME_STATUSES.has(normalize_text(runtime?.status).toLowerCase());
}

function calendar_event(case_item = {}) {
  const runtime = case_item?.sla_runtime || {};
  const due_date = parse_due_date(runtime?.due_at);
  if (!due_date || !is_open_runtime(runtime)) return null;

  return {
    id: `sla-${case_item?.application_id || case_item?.entity_profile_id || case_item?.case_code || due_date.getTime()}`,
    case_code: normalize_text(case_item?.case_code),
    case_title: normalize_text(case_item?.title),
    due_at: due_date,
    start: time_label(due_date),
    duration: '',
    title: normalize_text(case_item?.case_code)
      ? `SLA - ${case_item.case_code}`
      : `SLA - ${normalize_text(runtime?.sla_name || 'Case due')}`,
    meta: event_meta(case_item),
    card_class: 'case-owner-calendar-event-sla case-owner-calendar-event-sla-compact',
    style: {
      top: `${event_top_rem(due_date)}rem`,
      height: `${event_height_rem()}rem`,
    },
    is_overdue: runtime_status_label(runtime) === 'overdue'
      || ['breached', 'escalated'].includes(normalize_text(runtime?.status).toLowerCase()),
  };
}

function week_days_from(start_date, events = []) {
  const today = start_of_day(new Date());

  return Array.from({ length: 7 }, (_, index) => {
    const day_date = add_days(start_date, index);
    const day_events = events
      .filter((event) => same_day(event.due_at, day_date))
      .sort((left, right) => left.due_at - right.due_at);

    return {
      key: `${DAY_KEYS[day_date.getDay()]}-${day_date.toISOString().slice(0, 10)}`,
      label: day_head_label(day_date),
      is_today: same_day(day_date, today),
      date: day_date,
      events: day_events,
    };
  });
}

function day_view_days(day_date, events = []) {
  const today = start_of_day(new Date());
  return [{
    key: `day-${day_date.toISOString().slice(0, 10)}`,
    label: day_head_label(day_date),
    is_today: same_day(day_date, today),
    date: day_date,
    events: events
      .filter((event) => same_day(event.due_at, day_date))
      .sort((left, right) => left.due_at - right.due_at),
  }];
}

function month_days_from(month_start, events = []) {
  const grid_start = start_of_week(month_start);
  const today = start_of_day(new Date());

  return Array.from({ length: 42 }, (_, index) => {
    const day_date = add_days(grid_start, index);
    const day_events = events
      .filter((event) => same_day(event.due_at, day_date))
      .sort((left, right) => left.due_at - right.due_at);

    return {
      key: `month-${day_date.toISOString().slice(0, 10)}`,
      label: day_date.getDate(),
      date: day_date,
      is_today: same_day(day_date, today),
      in_month: day_date.getMonth() === month_start.getMonth()
        && day_date.getFullYear() === month_start.getFullYear(),
      events: day_events,
    };
  });
}

function summary_from(events = []) {
  const overdue_count = events.filter((event) => event.is_overdue).length;
  const due_today_count = events.filter((event) => same_day(event.due_at, new Date())).length;

  return {
    total_events: events.length,
    overdue: overdue_count,
    due_today: due_today_count,
  };
}

function create_owner_calendar_state() {
  const current_week_start = start_of_week(new Date());
  const current_month_start = start_of_month(new Date());
  return {
    view_mode: 'week',
    current_week_start,
    current_month_start,
    focused_day: start_of_day(new Date()),
    legend_items: [
      { label: 'SLA deadline', dot_class: 'case-owner-calendar-dot-sla' },
    ],
    time_slots: TIME_SLOTS,
    loading_calendar: false,
    calendar_error: '',
    calendar_events: [],
  };
}

export default {
  name: 'OwnerCalendar',
  components: {
    BlockingLoaderOverlay,
  },
  data: () => create_owner_calendar_state(),
  created() {
    this.load_calendar_data();
  },
  computed: {
    project_id() {
      return this.$route?.query?.project_id
        || this.$route?.query?.projectId
        || read_session_project_id();
    },
    visible_events() {
      const events = Array.isArray(this.calendar_events) ? this.calendar_events : [];

      if (this.view_mode === 'day') {
        return events.filter((event) => same_day(event.due_at, this.focused_day));
      }

      if (this.view_mode === 'month') {
        const month_end = new Date(this.current_month_start);
        month_end.setMonth(month_end.getMonth() + 1);
        return events.filter((event) => event.due_at >= this.current_month_start && event.due_at < month_end);
      }

      const week_end = add_days(this.current_week_start, 7);
      return events.filter((event) => event.due_at >= this.current_week_start && event.due_at < week_end);
    },
    week_days() {
      if (this.view_mode === 'day') {
        return day_view_days(this.focused_day, this.calendar_events);
      }

      return week_days_from(this.current_week_start, this.calendar_events);
    },
    month_days() {
      return month_days_from(this.current_month_start, this.calendar_events);
    },
    range_label() {
      if (this.view_mode === 'day') {
        return date_label(this.focused_day);
      }

      if (this.view_mode === 'month') {
        return month_label(this.current_month_start);
      }

      const week_end = add_days(this.current_week_start, 6);
      return range_label(this.current_week_start, week_end);
    },
    summary() {
      return summary_from(this.visible_events);
    },
    sync_label() {
      return this.loading_calendar
        ? 'Loading SLA deadlines'
        : 'Live SLA deadlines';
    },
    page_loading_message() {
      return 'Loading calendar...';
    },
    page_loading_detail() {
      return 'Pulling SLA deadlines and due items for your cases.';
    },
  },
  methods: {
    async load_calendar_data() {
      this.loading_calendar = true;
      this.calendar_error = '';

      try {
        const cases = await fetch_case_owner_cases(this.project_id);
        this.calendar_events = cases
          .map(calendar_event)
          .filter(Boolean)
          .sort((left, right) => left.due_at - right.due_at);
      } catch (error) {
        this.calendar_events = [];
        this.calendar_error = normalize_text(error?.message) || 'Failed to load SLA calendar.';
      } finally {
        this.loading_calendar = false;
      }
    },
    set_view_mode(mode) {
      this.view_mode = ['day', 'month'].includes(mode) ? mode : 'week';
      if (this.view_mode === 'day') {
        this.focused_day = start_of_day(new Date());
      }
    },
    previous_range() {
      if (this.view_mode === 'day') {
        this.focused_day = add_days(this.focused_day, -1);
        return;
      }

      if (this.view_mode === 'month') {
        const previous_month = new Date(this.current_month_start);
        previous_month.setMonth(previous_month.getMonth() - 1);
        this.current_month_start = start_of_month(previous_month);
        return;
      }

      this.current_week_start = add_days(this.current_week_start, -7);
    },
    next_range() {
      if (this.view_mode === 'day') {
        this.focused_day = add_days(this.focused_day, 1);
        return;
      }

      if (this.view_mode === 'month') {
        const next_month = new Date(this.current_month_start);
        next_month.setMonth(next_month.getMonth() + 1);
        this.current_month_start = start_of_month(next_month);
        return;
      }

      this.current_week_start = add_days(this.current_week_start, 7);
    },
    go_to_today() {
      this.focused_day = start_of_day(new Date());
      this.current_week_start = start_of_week(new Date());
      this.current_month_start = start_of_month(new Date());
    },
    open_event(event) {
      if (!normalize_text(event?.case_code)) return;
      this.$router.push({ name: 'ViewCaseDetails', params: { caseCode: event.case_code } });
    },
  },
  watch: {
    project_id() {
      this.load_calendar_data();
    },
  },
};
